import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ReactGA from 'react-ga';
import { currentUser, selectScreenSize } from 'redux/session/selectors';
import { PartialHeaderLeftSideNoPanelLayout } from 'layouts/partialHeader.leftSideNoPanel.layout';
import {
  Profile,
  ElevenHundredDetails,
  VerifyEmail,
  UpdateDBYDContact,
  Completed,
  ProgressIndicatorVertical,
  ProgressIndicatorHorizontal,
  ConfirmLinkingModal
} from './components';
import { Button } from 'carbon-components-react';
import { UserAvatar32, ArrowLeft16 } from '@carbon/icons-react';
import { SCREEN_SIZE } from 'types';
import { ProfileFormValues } from './localStore/types';
import { useEmptyCallback } from 'utilities/hooks.util';
import { useSaga } from 'use-reducer-saga';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import {
  saveUserProfileStart,
  requestUserLinkingStart,
  verifyUserLinkingStart,
  setEmailLinkingStart,
  importEnquiriesStart,
  confirmUserLinkingStart,
  fetchImportEnquiriesStatusStart
} from './localStore/actions';

const Onboarding: React.FC = () => {
  const history = useHistory();
  const user = useSelector(currentUser);
  const screenSize = useSelector(selectScreenSize);

  if (user.is_linked && user.trial_organisation_name && user.mobile_number) {
    history.push('/');
  }

  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const {
    saveUserProfileResponse,
    isSavingUserProfile,
    requestUserLinkingResponse,
    requestUserLinkingError,
    isRequestingUserLinking,
    verifyUserLinkingResponse,
    verifyUserLinkingError,
    isVerifyingUserLinking,
    setEmailLinkingResponse,
    isSetEmailLinking,
    confirmUserLinkingResponse,
    confirmUserLinkingError,
    isConfirmingUserLinking,
    importEnquiriesResponse,
    isImportingEnquiries,
    fetchImportEnquiriesStatus,
    isFetchingImportEnquiriesStatus
  } = localStore;

  const isTrialUser = user?.is_trial || false;
  const isExternalUser = user?.is_as_external_user || false;

  const organisationName = user?.Organisation?.organisation_name
    ? user.Organisation.organisation_name
    : '-';
  const companyNameToDisplay =
    user?.plan_type_id !== 1 && !isTrialUser
      ? organisationName
      : user?.trial_organisation_name;

  const profileFormInitialValues = {
    companyName: companyNameToDisplay,
    contactPhoneNumber: user?.mobile_number,
    jobTitle: user?.job_title,
    industry: user?.organisation_type,
    companySize: user?.organisation_size,
    estimatedEnquiries: user?.estimate_enquiry,
    isTrialUser: isTrialUser
  };

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [profileValues, setProfileValues] = useState<ProfileFormValues>(
    profileFormInitialValues
  );
  const [elevenHundredDetailsValues, setElevenHundredDetailsValues] =
    useState<string>('');
  const [importRecentEnquiries, setImportRecentEnquiries] = useState<boolean>(
    !isExternalUser ? true : false
  );
  const [pageTitle, setPageTitle] = useState<string>(
    'Welcome. Tell us about you.'
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOnSubmitStep1 = (values: any) => {
    addGAEvent('Clicked Next button');
    setProfileValues(values);
    const params = {
      trial_organisation_name: isTrialUser ? values.companyName : undefined,
      mobile_number: values.contactPhoneNumber,
      phone_number: values.contactPhoneNumber,
      job_title: values.jobTitle,
      organisation_size: isTrialUser ? values.companySize : undefined,
      organisation_type: isTrialUser ? values.industry : undefined,
      estimate_enquiry: isTrialUser ? values.estimatedEnquiries : undefined
    };
    localDispatch(saveUserProfileStart(params));
  };

  const handleOnSubmitStep2 = (values: any) => {
    addGAEvent('Clicked Next button');
    setElevenHundredDetailsValues(values.dybdUsername);
    const params = { email: values.dybdUsername };
    localDispatch(requestUserLinkingStart(params));
  };

  const handleOnSubmitStep3 = (values: any) => {
    addGAEvent('Clicked Next button');
    const params = {
      email: elevenHundredDetailsValues,
      verificationCode: values.codes
    };
    localDispatch(verifyUserLinkingStart(params));
  };

  const handleOnSubmitStep4 = (values: any) => {
    addGAEvent('Clicked Continue button');
    if (values.updateMyDBYDContactEmail) {
      localDispatch(setEmailLinkingStart({}));
    }
  };

  const handleOnSubmitModal = () => {
    addGAEvent('Clicked Yes, replace button');
    localDispatch(confirmUserLinkingStart({}));
  };

  const initiateImportEnquiries = useEmptyCallback(() => {
    localDispatch(importEnquiriesStart({}));
  });

  const getImportEnquiriesStatus = useEmptyCallback(() => {
    localDispatch(
      fetchImportEnquiriesStatusStart({ type: 'IMPORT_ENQUIRIES' })
    );
  });

  const handleClickGoToLocate = (isSkip: boolean = false) => {
    addGAEvent(
      !isSkip
        ? 'Clicked Start using SmarterWX Locate button'
        : 'Clicked Skip and import in the background link'
    );
    window.location.href = '/';
  };

  const handleCancelButtonClick = () => {
    addGAEvent('Clicked Cancel button');
    window.location.href = window.location.pathname;
  };

  const handleRequestNewCode = () => {
    addGAEvent('Clicked Request new code link');
    const params = { email: elevenHundredDetailsValues };
    localDispatch(requestUserLinkingStart(params));
  };

  const handleBackButtonClick = () => {
    addGAEvent('Clicked Back button');
    setCurrentStep(currentStep - 1);
  };

  const addGAEvent = (action: string) => {
    ReactGA.event({
      category: 'Onboarding',
      action: action,
      label: `Step ${currentStep}`
    });
  };

  useEffect(() => {
    if (!isSavingUserProfile && saveUserProfileResponse) {
      setCurrentStep(2);
    }
  }, [saveUserProfileResponse, isSavingUserProfile]);

  useEffect(() => {
    if (!isRequestingUserLinking && requestUserLinkingResponse) {
      setCurrentStep(3);
    }
  }, [requestUserLinkingResponse, isRequestingUserLinking]);

  useEffect(() => {
    if (
      !isVerifyingUserLinking &&
      verifyUserLinkingResponse &&
      !verifyUserLinkingError
    ) {
      setCurrentStep(4);
    } else if (
      verifyUserLinkingError === 'EXISTING_SUBSCRIPTION_ERROR' &&
      !verifyUserLinkingResponse &&
      !isVerifyingUserLinking
    ) {
      setIsModalOpen(true);
    }
  }, [
    verifyUserLinkingResponse,
    verifyUserLinkingError,
    isVerifyingUserLinking
  ]);

  useEffect(() => {
    if (!isConfirmingUserLinking && confirmUserLinkingResponse) {
      setIsModalOpen(false);
      setCurrentStep(4);
    } else if (confirmUserLinkingError) {
      setIsModalOpen(false);
    }
  }, [
    confirmUserLinkingResponse,
    isConfirmingUserLinking,
    confirmUserLinkingError
  ]);

  useEffect(() => {
    if (!isSetEmailLinking && setEmailLinkingResponse) {
      if (importRecentEnquiries) {
        initiateImportEnquiries();
      } else {
        setCurrentStep(5);
      }
    }
  }, [
    setEmailLinkingResponse,
    isSetEmailLinking,
    importRecentEnquiries,
    initiateImportEnquiries
  ]);

  useEffect(() => {
    if (!isImportingEnquiries && importEnquiriesResponse) {
      setCurrentStep(5);
      getImportEnquiriesStatus();
    }
  }, [isImportingEnquiries, importEnquiriesResponse, getImportEnquiriesStatus]);

  useEffect(() => {
    if (!isFetchingImportEnquiriesStatus && fetchImportEnquiriesStatus) {
      if (fetchImportEnquiriesStatus.status !== 'completed') {
        setTimeout(() => {
          getImportEnquiriesStatus();
        }, 5000);
      }
    }
  }, [
    isFetchingImportEnquiriesStatus,
    fetchImportEnquiriesStatus,
    getImportEnquiriesStatus
  ]);

  useEffect(() => {
    if (currentStep === 1) {
      setPageTitle('Welcome. Tell us about you.');
    } else if (currentStep === 2) {
      setPageTitle('Enter your BYDA details');
    } else if (currentStep === 3) {
      setPageTitle('Verify your email account');
    } else if (currentStep === 4) {
      setPageTitle('Verified');
    } else if (currentStep >= 5) {
      setPageTitle('Getting things ready');
    }
  }, [currentStep]);

  return (
    <div id="Onboarding" data-testid="Onboarding" className="onboarding">
      <PartialHeaderLeftSideNoPanelLayout enableHeaderGlobalBar={false}>
        <div className="onboarding-content-container">
          <div
            className={`form-section ${
              currentStep !== 5 ? '' : 'form-section-bigger'
            }`}
          >
            {screenSize === SCREEN_SIZE.SM &&
              (currentStep === 2 || currentStep === 3) && (
                <div className="back-button-small-screen padding">
                  <Button
                    kind="tertiary"
                    size={'md'}
                    renderIcon={ArrowLeft16}
                    hasIconOnly={true}
                    className="back-button"
                    onClick={handleBackButtonClick}
                    iconDescription="Back"
                    disabled={isVerifyingUserLinking || isRequestingUserLinking}
                  />
                </div>
              )}
            <div className="title padding">{pageTitle}</div>
            {screenSize === SCREEN_SIZE.SM && currentStep !== 5 && (
              <div>
                <ProgressIndicatorHorizontal currentStep={currentStep} />
              </div>
            )}
            <div className={`${currentStep !== 5 ? 'form-body' : ''}`}>
              {currentStep <= 3 && (
                <div className="user-name padding">
                  <UserAvatar32 /> {user?.first_name} {user?.last_name}
                </div>
              )}
              {currentStep === 1 && (
                <Profile
                  onSubmit={handleOnSubmitStep1}
                  profileValues={profileValues}
                  isTrialUser={isTrialUser}
                  isSavingUserProfile={isSavingUserProfile}
                />
              )}
              {currentStep === 2 && (
                <ElevenHundredDetails
                  onSubmit={handleOnSubmitStep2}
                  onClickBack={handleBackButtonClick}
                  onClickCancel={handleCancelButtonClick}
                  elevenHundredDetailsValues={elevenHundredDetailsValues}
                  isRequestingUserLinking={isRequestingUserLinking}
                  requestUserLinkingError={requestUserLinkingError}
                />
              )}
              {currentStep === 3 && (
                <VerifyEmail
                  onSubmit={handleOnSubmitStep3}
                  onClickBack={handleBackButtonClick}
                  onClickCancel={handleCancelButtonClick}
                  onClickRequestNewCode={handleRequestNewCode}
                  elevenHundredDetailsValues={elevenHundredDetailsValues}
                  isVerifyingUserLinking={isVerifyingUserLinking}
                  verifyUserLinkingError={verifyUserLinkingError}
                />
              )}
              {currentStep === 4 && (
                <UpdateDBYDContact
                  onSubmit={handleOnSubmitStep4}
                  isSetEmailLinking={isSetEmailLinking}
                  isExternalUser={isExternalUser}
                  importRecentEnquiries={importRecentEnquiries}
                  setImportRecentEnquiries={setImportRecentEnquiries}
                  userFirstName={user.first_name}
                  userLocateEmail={user.email}
                  isImportingEnquiries={isImportingEnquiries || false}
                />
              )}
              {currentStep === 5 && (
                <Completed
                  importRecentEnquiries={importRecentEnquiries}
                  onClickGoToLocateButton={handleClickGoToLocate}
                  importEnquiriesResponse={importEnquiriesResponse}
                  isImportingEnquiries={isImportingEnquiries || false}
                  fetchImportEnquiriesStatus={fetchImportEnquiriesStatus}
                  isFetchingImportEnquiriesStatus={
                    isFetchingImportEnquiriesStatus || false
                  }
                />
              )}
            </div>
          </div>
          {screenSize !== SCREEN_SIZE.SM && currentStep !== 5 && (
            <div className="progress-section">
              <ProgressIndicatorVertical currentStep={currentStep} />
            </div>
          )}
        </div>
        <ConfirmLinkingModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onClickSubmit={handleOnSubmitModal}
          isConfirmingUserLinking={isConfirmingUserLinking}
        />
      </PartialHeaderLeftSideNoPanelLayout>
    </div>
  );
};

export default React.memo(Onboarding);
